import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jobsAPI, usersAPI, projectsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import useDesktopCheck from '../hooks/useDesktopCheck';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Button from '../components/UI/Button';
import ConfirmationModal from '../components/UI/ConfirmationModal';
import JustifiedGrid from '../components/UI/JustifiedGrid';
import mockProjects from '../data/mockProjects.json';
import {
  Plus,
  FileVideo,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Trash2
} from 'lucide-react';

const RecentProjectPage = () => {
  const [projectItems, setprojectItems] = React.useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [projectToDelete, setProjectToDelete] = React.useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Check if user is on mobile or portrait orientation
  const isMobileOrPortrait = useDesktopCheck();

  const handleOpenLiteView = (project) => {
    window.open(`/lite-view/${project._id}`);
  };

  const handleOpenCanvas = (project) => {
    window.open(`/canvas/${project._id}`);
  };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['userStats'],
    queryFn: usersAPI.getStats
  });

  const { data: recentJobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['recentJobs'],
    queryFn: () => jobsAPI.getAll({ limit: 5, sort: '-createdAt' })
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsAPI.getAllProjects
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: (projectId) => projectsAPI.deleteProject(projectId),
    onSuccess: () => {
      // Invalidate and refetch projects
      queryClient.invalidateQueries(['projects']);
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    },
    onError: (error) => {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    }
  });

  if (statsLoading || jobsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete) {
      deleteProjectMutation.mutate(projectToDelete._id);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const createNewProject = async () => {
    // Check if user is on mobile or portrait orientation before creating project
    if (isMobileOrPortrait) {
      toast.error('Creating projects requires a desktop or laptop with a landscape display. Please switch to a larger device to continue.', {
        duration: 5000,
        icon: '💻',
      });
      return;
    }

    const projectData = {
      // id: `project_${Date.now()}_${Math.random().toString(36).substring(2)}`,
      name: 'Untitled Project',
      canvas: {
        width: 1920,
        height: 1080,
        backgroundColor: '#000000'
      },
      duration: 10,
      layers: []
    };

    const projectCreated = await projectsAPI.createNewProject(projectData);

    if (projectCreated?.data?.status === 'success') {
      const projectId = projectCreated.data.project._id;
      // console.log("New project created with ID:", projectCreated.data.project);
      // Redirect to canvas with new project ID
      window.open(`/canvas/${projectId}`);
    } else {
      alert('Error creating project. Please try again.');
    }
  }

  const userStats = stats?.data || {};
  const jobs = recentJobs?.data?.allRenders || [];

  const projects = projectsData?.data?.projects || [];
  // console.log("Projects:", projects);

  return (
    <div className="min-h-screen bg-ag-black-800 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-sm sm:text-base text-gray-400 mt-1">
            Here's what's happening with your projects
          </p>
        </div>
        {/* create a new id for the new project */}
        <Button onClick={createNewProject} className="bg-ag-green text-ag-black-800 hover:bg-ag-green/90 w-[50%] sm:w-auto">
          <Plus size={20} className="mr-2" />
          New Project
        </Button>
      </div>

      {/* Recent Projects */}
      <div className="bg-ag-black-700 rounded-lg border border-ag-black-400 p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-white">Recent Projects</h2>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-10 sm:py-16">
            <FileVideo className="mx-auto text-gray-500 mb-4" size={40} />
            <h3 className="text-base sm:text-lg font-medium text-white mb-2">
              No projects yet
            </h3>
            <p className="text-sm sm:text-base text-gray-400 mb-6 px-4">
              Get started by creating your first video project
            </p>
            <Link to="/templates">
              <Button className="bg-ag-green text-ag-black-800 hover:bg-ag-green/90">Browse Templates</Button>
            </Link>
          </div>
        ) : (
          <JustifiedGrid
            items={projects}
            targetRowHeight={250}
            gap={6}
            getAspectRatio={(project) => (project.canvas?.width || 1920) / (project.canvas?.height || 1080)}
            keyExtractor={(project) => project._id || project.id}
            renderItem={(project) => (
              <div className="group relative rounded-xl overflow-hidden cursor-pointer bg-ag-black-900 w-full h-full">
                {/* Thumbnail */}
                {project.thumbnailUrl ? (
                  <img
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    src={project.lastModified ? `${project.thumbnailUrl}?v=${new Date(project.lastModified).getTime()}` : project.thumbnailUrl}
                    alt={project.name}
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-ag-black-800">
                    <FileVideo className="text-gray-600" size={48} />
                  </div>
                )}

                {/* Hover overlay with action buttons */}
                {(() => {
                  const isPortrait = (project.canvas?.height || 1080) > (project.canvas?.width || 1920);
                  return (
                    <div className={`absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10 ${isPortrait ? 'flex-col gap-2 px-3' : 'gap-2 px-4'}`}>
                      <Button
                        onClick={() => handleOpenLiteView(project)}
                        className={`bg-ag-green text-ag-black-800 hover:bg-ag-green/90 border-0 font-semibold ${isPortrait ? 'w-full text-xs' : ''}`}
                        size="sm"
                      >
                        {isPortrait ? 'Lite View' : 'Open in Lite View'}
                      </Button>
                      <button
                        onClick={() => handleOpenCanvas(project)}
                        className={`text-gray-300 hover:text-white rounded border border-gray-600 hover:border-gray-400 bg-ag-black-700/60 transition-colors ${isPortrait ? 'w-full text-xs py-1.5 px-2' : 'hidden sm:block text-sm py-1.5 px-3'}`}
                      >
                        {isPortrait ? 'Canvas' : 'Canvas (Advanced)'}
                      </button>
                    </div>
                  );
                })()}

                {/* Delete button */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteClick(project); }}
                  className="absolute top-2 right-2 p-1.5 text-white/60 hover:text-red-400 hover:bg-red-500/20 rounded-full backdrop-blur-sm bg-black/30 transition-colors z-20 opacity-0 group-hover:opacity-100"
                  title="Delete project"
                >
                  <Trash2 size={14} />
                </button>

                {/* Bottom gradient + name overlay */}
                <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none">
                  <div className="bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-10 px-3 pb-3">
                    <p className="text-sm font-medium text-white truncate">
                      {project.title || project.name}
                    </p>
                    <p className="text-xs text-white/50 truncate">
                      {project.lastModified
                        ? new Date(project.lastModified).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                        : 'No modification date'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${projectToDelete?.name || 'this project'}"? This action cannot be undone and will delete all associated data.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default RecentProjectPage;
